/* ============================================================================
   SWD PDP Media Content Layer — Interaktion
   - Hotspot-Popover: Tap/Klick-Toggle (Touch-robust), Outside-Click, Escape,
     aria-expanded-Sync, Kanten-Flip (Popover nach unten wenn oben kein Platz).
   - Einblend-Animation: an Sichtbarkeit der Slide gekoppelt (IntersectionObserver),
     gestaffelt pro Element. Respektiert prefers-reduced-motion.
   Dependency-frei, idempotent, re-init-fähig (shopify:section:load).
   Doku: agency/tools/shopify/themes/horizon/SWD-bausteine/SWD-pdp-media-layer.md
   ========================================================================== */

(() => {
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Hotspots ---------------------------------------------------------- */
  function closeAllHotspots(except) {
    document.querySelectorAll('.swd-ml__hotspot[data-open="true"]').forEach((hs) => {
      if (hs === except) return;
      hs.removeAttribute('data-open');
      const btn = hs.querySelector('.swd-ml__hotspot-dot');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    });
  }

  function positionPopover(hs) {
    // Kanten-Flip: wenn oben zu wenig Platz, Popover nach unten klappen.
    const pop = hs.querySelector('.swd-ml__hotspot-pop');
    if (!pop) return;
    hs.removeAttribute('data-flip');
    const rect = hs.getBoundingClientRect();
    const galleryTop = hs.closest('.swd-ml')?.getBoundingClientRect().top ?? 0;
    if (rect.top - galleryTop < pop.offsetHeight + 24) {
      hs.setAttribute('data-flip', 'down');
    }
  }

  function onHotspotClick(e) {
    const btn = e.target.closest('.swd-ml__hotspot-dot');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const hs = btn.closest('.swd-ml__hotspot');
    const open = hs.getAttribute('data-open') === 'true';
    closeAllHotspots(hs);
    if (open) {
      hs.removeAttribute('data-open');
      btn.setAttribute('aria-expanded', 'false');
    } else {
      positionPopover(hs);
      hs.setAttribute('data-open', 'true');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  /* ---- Einblend-Animation ------------------------------------------------ */
  let io = null;
  function ensureObserver() {
    if (io || REDUCED || !('IntersectionObserver' in window)) return;
    io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const root = entry.target;
            root.setAttribute('data-swd-ml-in', 'true');
            root.querySelectorAll('[data-swd-ml-el]').forEach((el, i) => {
              el.style.setProperty('--swd-ml-delay', `${Math.min(i * 70, 420)}ms`);
            });
            io.unobserve(root);
          }
        });
      },
      { threshold: 0.35 }
    );
  }

  function initAnimations(scope) {
    ensureObserver();
    (scope || document).querySelectorAll('.swd-ml--animate:not([data-swd-ml-in])').forEach((root) => {
      if (io) io.observe(root);
      else root.setAttribute('data-swd-ml-in', 'true'); // Fallback: sofort sichtbar
    });
  }

  /* ---- Rechts-Überhang: Slide kann breiter sein als ihr Scrollport --------
     (Horizon beschneidet das Bild mobil seitlich). Gemessenen Überhang als
     CSS-Var setzen, damit Rechts-Zonen an der SICHTBAREN Kante hängen. */
  function pinRightZones(scope) {
    (scope || document).querySelectorAll('slideshow-slide .swd-ml').forEach((ml) => {
      const slide = ml.closest('slideshow-slide');
      const port = slide && (slide.closest('slideshow-slides, slideshow-container') || slide.parentElement);
      if (!slide || !port) return;
      // sichtbare rechte Kante: Scrollport ODER Dokument (der Body clippt
      // überbreite Slides — der Port selbst kann mit überstehen)
      const visRight = Math.min(port.getBoundingClientRect().right, document.documentElement.clientWidth);
      const over = Math.max(0, Math.round(slide.getBoundingClientRect().right - visRight));
      ml.style.setProperty('--swd-ml-overhang', over + 'px');
    });
  }
  /* ---- Caption-Hoehe messen, damit Bottom-Zonen wirklich ausweichen -------
     Vorher stand im CSS ein fester Zuschlag von 34px. Der passte zur einzeiligen
     Caption; sobald sie umbricht (zweizeilig = 74px), lagen die Bottom-Zonen im
     Balken. CSS kann Geschwister nicht messen, also macht es JS und reicht den
     Wert als Variable weiter. ResizeObserver statt einmaliger Messung, weil die
     Zeilenzahl mit der Viewport-Breite kippt. */
  function beobachteCaptions(scope) {
    if (!window.ResizeObserver) return;
    (scope || document).querySelectorAll('.swd-ml').forEach((ml) => {
      const cap = ml.querySelector(':scope .swd-ml__caption--bottom');
      if (!cap || cap.dataset.swdCapObs) return;
      cap.dataset.swdCapObs = '1';
      const setze = () => ml.style.setProperty('--swd-ml-cap-h', Math.round(cap.getBoundingClientRect().height) + 'px');
      setze();
      new ResizeObserver(setze).observe(cap);
    });
  }

  let pinScheduled = false;
  function schedulePin() {
    if (pinScheduled) return;
    pinScheduled = true;
    requestAnimationFrame(() => { pinScheduled = false; pinRightZones(document); });
  }

  /* ---- Bootstrap --------------------------------------------------------- */
  function init(scope) {
    initAnimations(scope);
    pinRightZones(scope);
    beobachteCaptions(scope);
  }

  // Globale, delegierte Listener (nur einmal binden) — überlebt Section-Rerender.
  if (!window.__swdMlBound) {
    window.__swdMlBound = true;
    document.addEventListener('click', (e) => {
      if (e.target.closest('.swd-ml__hotspot-dot')) {
        onHotspotClick(e);
      } else if (!e.target.closest('.swd-ml__hotspot-pop')) {
        closeAllHotspots(null);
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllHotspots(null);
    });
    // Theme-Editor / dynamische Sections neu initialisieren
    document.addEventListener('shopify:section:load', (e) => init(e.target));
    // Überhang bei Resize/Slider-Scroll nachführen
    window.addEventListener('resize', schedulePin, { passive: true });
    document.addEventListener('scroll', schedulePin, { passive: true, capture: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }
})();
